import React, { useState } from 'react';
import './DataDeletions.css';

const DataDeletions = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    reason: '',
    confirmation: false
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.confirmation) {
      setError('Please confirm that you are the data subject or authorized representative');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Create the data deletion request payload
      const deletionRequest = {
        request_id: `del_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        request_type: 'data_deletion',
        data_subject: {
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone: formData.phone
        },
        request_reason: formData.reason,
        timestamp: new Date().toISOString(),
        status: 'pending',
        platform: 'web',
        domain: 'https://summitvisio.com'
      };

      // Send to your backend endpoint (you'll need to implement this)
      const response = await fetch('/api/data-deletions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(deletionRequest)
      });

      if (!response.ok) {
        throw new Error('Failed to submit deletion request');
      }

      // Also send to Meta's data deletion endpoint if needed
      await fetch('https://graph.facebook.com/v18.0/data_deletion_requests', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          app_id: '1483694403123958',
          request_id: deletionRequest.request_id,
          confirmation_code: deletionRequest.request_id.split('_')[1]
        })
      });

      setIsSubmitted(true);
    } catch (err) {
      console.error('Data deletion request error:', err);
      setError('Failed to submit data deletion request. Please try again or contact support.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="data-deletions-container">
        <div className="success-container">
          <div className="success-icon">✓</div>
          <h1>Data Deletion Request Submitted</h1>
          <p>Your data deletion request has been successfully submitted.</p>
          <div className="request-details">
            <h3>Request Details:</h3>
            <ul>
              <li><strong>Request ID:</strong> del_{Date.now()}_{Math.random().toString(36).substr(2, 9)}</li>
              <li><strong>Status:</strong> Pending</li>
              <li><strong>Submitted:</strong> {new Date().toLocaleString()}</li>
            </ul>
          </div>
          <p className="next-steps">
            You will receive a confirmation email shortly. We will process your request within 30 days as required by applicable privacy laws.
          </p>
          <a href="/" className="home-link">Return to Homepage</a>
        </div>
      </div>
    );
  }

  return (
    <div className="data-deletions-container">
      <div className="page-header">
        <h1>Data Deletion Request</h1>
        <p>Submit a request to delete your personal data from our systems and Meta platforms.</p>
      </div>

      <div className="content-wrapper">
        <div className="info-section">
          <h2>What This Request Covers</h2>
          <div className="data-types">
            <h3>Data Types to be Deleted:</h3>
            <ul>
              <li>Personal information (name, email, phone number)</li>
              <li>WhatsApp Business API data and conversations</li>
              <li>Account preferences and settings</li>
              <li>Usage data and analytics</li>
              <li>Support tickets and communications</li>
            </ul>
          </div>

          <div className="process-info">
            <h3>Processing Timeline:</h3>
            <ul>
              <li>Request confirmation: Within 24 hours</li>
              <li>Data review: Within 7 days</li>
              <li>Complete deletion: Within 30 days</li>
            </ul>
          </div>

          <div className="legal-info">
            <h3>Legal Rights:</h3>
            <p>You have the right to request deletion of your personal data under:</p>
            <ul>
              <li>GDPR (General Data Protection Regulation)</li>
              <li>CCPA (California Consumer Privacy Act)</li>
              <li>POPIA (Protection of Personal Information Act)</li>
            </ul>
          </div>
        </div>

        <div className="form-section">
          <h2>Data Deletion Request Form</h2>
          <form onSubmit={handleSubmit} className="deletion-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName">First Name *</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your first name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="lastName">Last Name *</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your last name"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="your.email@example.com"
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone Number</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="+27 12 345 6789"
              />
            </div>

            <div className="form-group">
              <label htmlFor="reason">Reason for Deletion Request *</label>
              <select
                id="reason"
                name="reason"
                value={formData.reason}
                onChange={handleInputChange}
                required
              >
                <option value="">Select a reason</option>
                <option value="privacy_concerns">Privacy Concerns</option>
                <option value="account_closure">Account Closure</option>
                <option value="data_accuracy">Data Accuracy Concerns</option>
                <option value="withdraw_consent">Withdraw Consent</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="confirmation"
                  checked={formData.confirmation}
                  onChange={handleInputChange}
                  required
                />
                <span className="checkmark"></span>
                I confirm that I am the data subject or an authorized representative, and I have the right to request deletion of this personal data.
              </label>
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <button
              type="submit"
              className="submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Deletion Request'}
            </button>
          </form>
        </div>
      </div>

      <div className="footer-info">
        <p>
          For questions about your data deletion request, please contact us at:
          <a href="mailto:privacy@summitvisio.com">privacy@summitvisio.com</a>
        </p>
      </div>
    </div>
  );
};

export default DataDeletions;
